/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { Enrollment, PaymentAndTaxStatus, PayoutReport, Invitation, PartnerId } from "../../models/model";
import { LocalizationInfo } from "@emt/emt-localization/lib/localization-info";
import { Metadata } from "@emt/emt-metadata-client";

export const STORE_ENROLLMENTS = "STORE_ENROLLMENTS";
export const COMPLETED_ENROLLMENTS = "COMPLETED_ENROLLMENTS";
export const ERROR_ENROLLMENTS = "ERROR_ENROLLMENTS";
export const ADD_ENROLLMENT = "ADD_ENROLLMENT";

export const STORE_INVITATIONS = "STORE_INVITATIONS";
export const COMPLETED_INVITATIONS = "COMPLETED_INVITATIONS";
export const ERROR_INVITATIONS = "ERROR_INVITATIONS";
export const REMOVE_INVITATION = "REMOVE_INVITATION";

export const STORE_PAYOUT_REPORTS = "STORE_PAYOUT_REPORTS";
export const COMPLETED_PAYOUT_REPORTS = "COMPLETED_PAYOUT_REPORTS";
export const ERROR_PAYOUT_REPORTS = "ERROR_PAYOUT_REPORTS";

export const STORE_BANK_AND_TAX_STATUS = "STORE_BANK_AND_TAX_STATUS";
export const COMPLETED_BANK_AND_TAX_STATUS = "COMPLETED_BANK_AND_TAX_STATUS";
export const ERROR_BANK_AND_TAX_STATUS = "ERROR_BANK_AND_TAX_STATUS";
export const UPDATE_BANK_AND_TAX_STATUS = "UPDATE_BANK_AND_TAX_STATUS";

export const STORE_METADATA = "STORE_METADATA";
export const COMPLETED_METADATA = "COMPLETED_METADATA";
export const ERROR_METADATA = "ERROR_METADATA";

export interface Action {
    readonly type: string;
}

export interface EnrollmentAction extends Action {
    readonly payload: Enrollment[];
}

export interface InvitationAction extends Action {
    readonly payload: Invitation[];
}

export interface PayoutReportAction extends Action {
    readonly payload: PayoutReport[];
}

export interface BankAndTaxStatusAction extends Action {
    readonly payload: PaymentAndTaxStatus[];
}

export interface UpdateBankAndTaxStatusAction extends Action {
    readonly payload: PaymentAndTaxStatus;
}
export interface MetadataAction extends Action {
    readonly payload: Metadata;
}

export interface LocalizationInfoAction extends Action {
    readonly payload: LocalizationInfo;
}

export interface AddEnrollmentAction extends Action {
    payload: Enrollment;
}

export interface RemoveInvitationAction extends Action {
    payload: RemoveInvitationPayload;
}

export interface RemoveInvitationPayload {
    offeringId: string;
    partnerId: PartnerId;
}

export class ActionDispatcher {
    public static storeEnrollments = (payload: Enrollment[]): EnrollmentAction => ({
        type: STORE_ENROLLMENTS,
        payload
    });

    public static addEnrollment = (payload: Enrollment): AddEnrollmentAction => ({
        type: ADD_ENROLLMENT,
        payload
    });

    public static completedEnrollments = (): Action => ({
        type: COMPLETED_ENROLLMENTS
    });

    public static errorEnrollments = (): Action => ({
        type: ERROR_ENROLLMENTS
    });

    public static storeInvitations = (payload: Invitation[]): InvitationAction => ({
        type: STORE_INVITATIONS,
        payload
    });

    public static completedInvitations = (): Action => ({
        type: COMPLETED_INVITATIONS
    });

    public static errorInvitations = (): Action => ({
        type: ERROR_INVITATIONS
    });

    public static removeInvitation = (payload: RemoveInvitationPayload): RemoveInvitationAction => ({
        type: REMOVE_INVITATION,
        payload: payload
    });

    public static storePayoutReports = (payload: PayoutReport[]): PayoutReportAction => ({
        type: STORE_PAYOUT_REPORTS,
        payload
    });

    public static completedPayoutReports = (): Action => ({
        type: COMPLETED_PAYOUT_REPORTS
    });

    public static errorPayoutReports = (): Action => ({
        type: ERROR_PAYOUT_REPORTS
    });

    public static storeBankAndTaxStatus = (payload: PaymentAndTaxStatus[]): BankAndTaxStatusAction => ({
        type: STORE_BANK_AND_TAX_STATUS,
        payload
    });

    public static completedBankAndTaxStatus = (): Action => ({
        type: COMPLETED_BANK_AND_TAX_STATUS
    });

    public static errorBankAndTaxStatus = (): Action => ({
        type: ERROR_BANK_AND_TAX_STATUS
    });

    public static updateBankAndTaxStatus = (payload: PaymentAndTaxStatus): UpdateBankAndTaxStatusAction => ({
        type: UPDATE_BANK_AND_TAX_STATUS,
        payload
    });

    public static storeMetadata = (payload: Metadata): MetadataAction => ({
        type: STORE_METADATA,
        payload
    });

    public static completedMetadata = (): Action => ({
        type: COMPLETED_METADATA
    });

    public static errorMetadata = (): Action => ({
        type: ERROR_METADATA
    });
}
